/**
 * PDF Page Picker Modal
 *
 * Shown after a user uploads a multi-page PDF (typically a CAD drawing set).
 * Renders thumbnails of every page via pdf.js and lets the user pick one;
 * the chosen page is extracted via pdf-lib into a new single-page PDF Blob
 * so the downstream storage + render pipeline always deals with a single
 * floor-plan sheet.
 *
 * Skipped when the PDF has only one page (caller checks and uploads directly).
 * Lazy-imports both pdf.js and pdf-lib — zero base-bundle cost for users who
 * never upload PDFs.
 */

import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { logger } from '@/utils/logger';
import { safeCanvasToDataURL } from '@/utils/compat';

const THUMB_TARGET_WIDTH = 320;
const CONCURRENT_RENDERS = 3;

interface PdfPagePickerModalProps {
  isOpen: boolean;
  pdfFile: File;
  onClose: () => void;
  onConfirm: (singlePagePdfBlob: Blob, pageNumber: number, totalPages: number) => void;
}

interface PageThumb {
  pageNumber: number;
  dataUrl: string | null; // null while rendering
}

/**
 * Narrow shape for an error that may carry a `name` (DOMException-like, e.g.
 * pdf.js `PasswordException`) and/or a `message`. Avoids `any` while keeping
 * the original runtime checks intact.
 */
interface NamedError {
  name?: string;
  message?: string;
}

const PdfPagePickerModal: React.FC<PdfPagePickerModalProps> = ({ isOpen, pdfFile, onClose, onConfirm }) => {
  const [pages, setPages] = useState<PageThumb[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [extracting, setExtracting] = useState(false);
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (!isOpen) return;
    cancelledRef.current = false;
    setError(null);
    setPages([]);

    (async () => {
      try {
        const pdfjs = await import('pdfjs-dist');
        const workerModule = await import('pdfjs-dist/build/pdf.worker.min.mjs?url');
        pdfjs.GlobalWorkerOptions.workerSrc = workerModule.default;

        const bytes = new Uint8Array(await pdfFile.arrayBuffer());
        const doc = await pdfjs.getDocument({ data: bytes }).promise;
        try {
          const numPages = doc.numPages;
          if (cancelledRef.current) return;

          // Seed placeholders so the grid skeleton renders immediately
          setPages(Array.from({ length: numPages }, (_, i) => ({
            pageNumber: i + 1,
            dataUrl: null
          })));

          // Render thumbnails in small concurrent batches — mobile CPUs choke
          // on a 20-page-at-once rasterize; batches feel snappy and keep UI
          // responsive between renders.
          for (let start = 0; start < numPages; start += CONCURRENT_RENDERS) {
            if (cancelledRef.current) break;
            const batch: Promise<void>[] = [];
            for (let i = start; i < Math.min(start + CONCURRENT_RENDERS, numPages); i++) {
              batch.push((async () => {
                const pageNum = i + 1;
                try {
                  const page = await doc.getPage(pageNum);
                  const baseViewport = page.getViewport({ scale: 1 });
                  const scale = baseViewport.width > 0 ? THUMB_TARGET_WIDTH / baseViewport.width : 1;
                  const viewport = page.getViewport({ scale });

                  const canvas = document.createElement('canvas');
                  canvas.width = Math.round(viewport.width);
                  canvas.height = Math.round(viewport.height);
                  const ctx = canvas.getContext('2d');
                  if (!ctx) return;
                  // pdf.js v5 requires both `canvas` and `canvasContext`
                  await page.render({ canvas, canvasContext: ctx, viewport }).promise;

                  if (cancelledRef.current) return;
                  const dataUrl = safeCanvasToDataURL(canvas, 'image/jpeg', 0.8);
                  setPages(prev => {
                    const next = [...prev];
                    next[i] = { pageNumber: pageNum, dataUrl };
                    return next;
                  });
                } catch (renderErr) {
                  logger.warn('Failed to render PDF page thumbnail', { pageNum, renderErr });
                }
              })());
            }
            await Promise.all(batch);
          }
        } finally {
          await doc.destroy();
        }
      } catch (err) {
        const namedErr = err as NamedError;
        if (namedErr?.name === 'PasswordException') {
          setError('This PDF is password-protected. Re-export without a password in your CAD tool, or save as SVG.');
        } else {
          logger.error('Failed to open PDF for page picker', { err });
          setError('Could not read this PDF. Try a different file or re-export it from your CAD tool.');
        }
      }
    })();

    return () => {
      cancelledRef.current = true;
    };
  }, [isOpen, pdfFile]);

  const handlePick = async (pageNumber: number) => {
    if (extracting) return;
    setExtracting(true);
    try {
      const { PDFDocument } = await import('pdf-lib');
      const bytes = new Uint8Array(await pdfFile.arrayBuffer());
      const srcDoc = await PDFDocument.load(bytes, { ignoreEncryption: false });
      const outDoc = await PDFDocument.create();
      const [copied] = await outDoc.copyPages(srcDoc, [pageNumber - 1]);
      outDoc.addPage(copied);
      const outBytes = await outDoc.save();
      // Copy into a fresh Uint8Array so the Blob ctor's BlobPart type is happy
      // regardless of pdf-lib's internal buffer typing
      const blob = new Blob([new Uint8Array(outBytes)], { type: 'application/pdf' });
      onConfirm(blob, pageNumber, pages.length);
    } catch (err) {
      const namedErr = err as NamedError;
      logger.error('Failed to extract PDF page', { err });
      setError(namedErr?.message?.includes('encrypted')
        ? 'This PDF is password-protected. Re-export without a password.'
        : 'Could not extract that page. Please try a different file.');
      setExtracting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[400] bg-black text-yellow-400 font-mono flex flex-col"
         style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
      {/* Header */}
      <div className="flex items-center justify-between p-3 pt-[max(12px,env(safe-area-inset-top))] border-b border-yellow-400/30">
        <button
          onClick={onClose}
          className="p-2 text-yellow-400 hover:text-white transition-colors"
          disabled={extracting}
          aria-label="Close"
        >
          <X size={24} />
        </button>
        <h2 className="text-lg font-bold" style={{ fontSize: 'min(3vw, 15px)' }}>
          Select a Page
        </h2>
        <div style={{ width: 40 }} />
      </div>

      {/* Subheader */}
      <div className="px-3 py-2 border-b border-yellow-400/20 text-center">
        <p className="text-xs text-yellow-400/80" style={{ fontSize: 'min(2.2vw, 11px)' }}>
          {pages.length > 0
            ? `${pages.length} pages — tap the floor-plan sheet you want`
            : 'Opening PDF…'}
        </p>
      </div>

      {/* Error */}
      {error && (
        <div className="m-3 p-3 bg-red-500/20 border border-red-400 rounded text-red-400 text-center"
             style={{ fontSize: 'min(2.4vw, 12px)' }}>
          {error}
        </div>
      )}

      {/* Thumbnails grid */}
      <div className="flex-1 overflow-y-auto p-3">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-w-4xl mx-auto">
          {pages.map((p) => (
            <button
              key={p.pageNumber}
              onClick={() => handlePick(p.pageNumber)}
              disabled={!p.dataUrl || extracting}
              className="flex flex-col items-center gap-1 p-2 rounded border-2 border-yellow-400/40 bg-black/50 hover:border-yellow-400 disabled:opacity-50 disabled:cursor-wait transition-colors"
              style={{ fontSize: 'min(2.2vw, 11px)' }}
            >
              <div className="w-full aspect-[1/1.3] bg-black/30 border border-yellow-400/20 flex items-center justify-center overflow-hidden">
                {p.dataUrl ? (
                  <img src={p.dataUrl} alt={`Page ${p.pageNumber}`} className="max-w-full max-h-full object-contain" />
                ) : (
                  <div className="w-6 h-6 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
                )}
              </div>
              <div className="font-semibold">Page {p.pageNumber}</div>
            </button>
          ))}
        </div>

        {extracting && (
          <div className="mt-4 text-center text-yellow-400/80" style={{ fontSize: 'min(2.4vw, 12px)' }}>
            Extracting selected page…
          </div>
        )}
      </div>
    </div>
  );
};

export default PdfPagePickerModal;
